import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';
import 'tippy.js/themes/light-border.css';

import {
  useGoogleCalendarAuthMutation,
  useGetGoogleEventsQuery
} from '../../../redux/apis/googleCalendarApi';
import { useIsLoggedInQuery } from '../../../redux/apis/authApi';

import './googleCalendar.scss';

moment.locale('en-GB');
const localizer = momentLocalizer(moment);

const toEventsPayload = (range) => {
  const startDate = Array.isArray(range) ? range[0] : range?.start;
  const endDate = Array.isArray(range) ? range[range.length - 1] : range?.end;

  return {
    timeMin: moment(startDate).format('YYYY-MM-DD'),
    timeMax: moment(endDate).add(1, 'day').format('YYYY-MM-DD')
  };
};

const getInitialEventsPayload = () =>
  toEventsPayload({
    start: moment().startOf('month').startOf('week').toDate(),
    end: moment().endOf('month').endOf('week').toDate()
  });

/* =======================
   Google Calendar Colors
======================= */
const GOOGLE_EVENT_COLORS = {
  1: '#7986CB',
  2: '#33B679',
  3: '#8E24AA',
  4: '#E67C73',
  5: '#F6BF26',
  6: '#F4511E',
  7: '#039BE5',
  8: '#616161',
  9: '#3F51B5',
  10: '#0B8043',
  11: '#D50000'
};

const DEFAULT_EVENT_COLOR = '#4285F4';

const EventAvatar = ({ title, image }) => {
  const letter = title?.charAt(0)?.toUpperCase();

  return (
    <div className={`gc-tooltip__avatar${image ? ' gc-tooltip__avatar--photo' : ''}`}>
      {image ? <img src={image} alt={title} /> : letter}
    </div>
  );
};

const EventTooltip = ({ event }) => (
  <div className="gc-tooltip" style={{ ['--gc-accent']: event.eventColor }}>
    <div className="gc-tooltip__header">
      <EventAvatar title={event.title} image={event.organizer?.photo} />

      <div className="gc-tooltip__header-text">
        <div className="gc-tooltip__title">{event.title}</div>
        <div className="gc-tooltip__pills" aria-label="Event timing">
          <span className="gc-tooltip__pill">
            <i className="icofont-ui-calendar" aria-hidden />
            <span>
              {moment(event.start).format('ddd, MMM D')}
              <span className="gc-tooltip__pill-sep" aria-hidden>
                ·
              </span>
              {moment(event.start).format('LT')} – {moment(event.end).format('LT')}
            </span>
          </span>
        </div>
      </div>
    </div>

    <div className="gc-tooltip__body">
      {event.organizer ? (
        <div className="gc-tooltip__row">
          <div className="gc-tooltip__row-icon" aria-hidden>
            <i className="icofont-user-alt-3" />
          </div>
          <div className="gc-tooltip__row-content">
            <div className="gc-tooltip__row-title">Organizer</div>
            <div className="gc-tooltip__row-value">{event.organizer.email}</div>
          </div>
        </div>
      ) : null}

      {event.description ? (
        <div className="gc-tooltip__row">
          <div className="gc-tooltip__row-icon" aria-hidden>
            <i className="icofont-quote-left" />
          </div>
          <div className="gc-tooltip__row-content">
            <div className="gc-tooltip__row-title">Description</div>
            <div
              className="gc-tooltip__row-value gc-tooltip__row-value--wrap"
              dangerouslySetInnerHTML={{ __html: event.description }}
            />
          </div>
        </div>
      ) : null}

      {event.attendees?.length ? (
        <div className="gc-tooltip__row">
          <div className="gc-tooltip__row-icon" aria-hidden>
            <i className="icofont-users-alt-2" />
          </div>
          <div className="gc-tooltip__row-content">
            <div className="gc-tooltip__row-title">Guests</div>
            <div className="gc-tooltip__guest-box" role="list" aria-label="Guests">
              {event.attendees.map((a) => (
                <span key={a.email} className="gc-tooltip__guest" role="listitem" title={a.email}>
                  {a.email}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>

    {event.htmlLink ? (
      <div className="gc-tooltip__footer">
        <a className="gc-tooltip__btn" href={event.htmlLink} target="_blank" rel="noreferrer">
          <span>Open in Google Calendar</span>
          <i className="icofont-external-link" aria-hidden />
        </a>
      </div>
    ) : null}
  </div>
);

const GoogleCalendar = () => {
  const { data: loginData, refetch: refetchLogin } = useIsLoggedInQuery();
  const [googleCalendarAuth, { isLoading: authLoading }] = useGoogleCalendarAuthMutation();
  const [eventsRange, setEventsRange] = useState(getInitialEventsPayload);

  const {
    data,
    isLoading: eventsLoading,
    isFetching,
    refetch: refetchEvents
  } = useGetGoogleEventsQuery(eventsRange, {
    skip: !loginData?.data?.isCalendarConnected
  });

  const [events, setEvents] = useState([]);

  useEffect(() => {
    refetchLogin();
    if (loginData?.data?.isCalendarConnected) {
      refetchEvents();
    }
  }, []);

  /* =======================
       Map Google Events
    ======================= */
  useEffect(() => {
    if (!Array.isArray(data?.data)) return;

    if (data.data.length) {
      const mapped = data.data.map((e) => {
        const isAllDay = !!e.start.date && !e.start.dateTime;

        const start = new Date(e.start.dateTime || e.start.date);
        let end = new Date(e.end.dateTime || e.end.date);

        // 🔥 Google all-day end date is exclusive
        if (isAllDay) {
          end = moment(end).subtract(1, 'day').toDate();
        }

        return {
          ...e,
          title: e.summary,
          start,
          end,
          allDay: isAllDay,
          eventColor: e.colorId ? GOOGLE_EVENT_COLORS[e.colorId] : DEFAULT_EVENT_COLOR
        };
      });

      setEvents(mapped);
      return;
    }

    setEvents([]);
  }, [data]);

  const handleConnect = async () => {
    const res = await googleCalendarAuth();
    if (res?.data?.success) window.location.href = res.data.data;
  };

  const eventStyleGetter = (event) => {
    const hex = String(event.eventColor || DEFAULT_EVENT_COLOR).replace('#', '');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return {
      style: {
        backgroundColor: event.eventColor,
        color: luminance > 0.72 ? '#3f2d12' : '#fff',
        border: 'none'
      }
    };
  };

  return (
    <div className="google-calendar-page">
      <div className="google-calendar-page__header">
        <div>
          <h2 className="google-calendar-page__title">Calendar</h2>
          <p className="google-calendar-page__subtitle">
            {loginData?.data?.isCalendarConnected
              ? 'Your Google Calendar events'
              : 'Connect Google Calendar to view events here.'}
          </p>
        </div>
        <div className="google-calendar-page__status">
          {loginData?.data?.isCalendarConnected ? (
            <span className="gc-badge gc-badge--success">
              <span className="gc-badge__dot" aria-hidden />
              Connected
            </span>
          ) : (
            <span className="gc-badge gc-badge--neutral">
              <span className="gc-badge__dot" aria-hidden />
              Not connected
            </span>
          )}
        </div>
      </div>

      {!loginData?.data?.isCalendarConnected ? (
        <div className="gc-connect">
          <div className="gc-connect__content">
            <span className="gc-connect__icon" aria-hidden>
              <i className="icofont-calendar" />
            </span>

            <h3 className="gc-connect__title">Connect your Google Calendar</h3>
            <p className="gc-connect__desc">
              Sync meetings, reminders, and schedules so everything lives in one place — right
              inside your workspace.
            </p>

            <ul className="gc-connect__features" aria-label="What you get">
              <li className="gc-connect__feature">
                <span
                  className="gc-connect__feature-icon gc-connect__feature-icon--indigo"
                  aria-hidden
                >
                  <i className="icofont-ui-calendar" />
                </span>
                <span className="gc-connect__feature-text">
                  <span className="gc-connect__feature-title">Month, week & day views</span>
                  <span className="gc-connect__feature-sub">
                    See your schedule the way you prefer
                  </span>
                </span>
              </li>
              <li className="gc-connect__feature">
                <span
                  className="gc-connect__feature-icon gc-connect__feature-icon--teal"
                  aria-hidden
                >
                  <i className="icofont-users-alt-2" />
                </span>
                <span className="gc-connect__feature-text">
                  <span className="gc-connect__feature-title">Organizer, guests & details</span>
                  <span className="gc-connect__feature-sub">Full event context at a glance</span>
                </span>
              </li>
              <li className="gc-connect__feature">
                <span
                  className="gc-connect__feature-icon gc-connect__feature-icon--amber"
                  aria-hidden
                >
                  <i className="icofont-external-link" />
                </span>
                <span className="gc-connect__feature-text">
                  <span className="gc-connect__feature-title">Open in Google Calendar</span>
                  <span className="gc-connect__feature-sub">Jump straight to the source event</span>
                </span>
              </li>
            </ul>

            <button
              type="button"
              onClick={handleConnect}
              disabled={authLoading}
              className="gc-connect__btn"
            >
              {authLoading ? (
                <>
                  <span className="gc-connect__btn-spinner" aria-hidden />
                  Connecting…
                </>
              ) : (
                <>
                  <span className="gc-connect__btn-g" aria-hidden>
                    <svg width="16" height="16" viewBox="0 0 48 48">
                      <path
                        fill="#EA4335"
                        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                      />
                      <path
                        fill="#4285F4"
                        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                      />
                      <path
                        fill="#34A853"
                        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                      />
                    </svg>
                  </span>
                  Connect Google Calendar
                </>
              )}
            </button>

            <div className="gc-connect__footnote">
              <i className="icofont-lock" aria-hidden />
              Secure OAuth · We never see or store your password
            </div>
          </div>
        </div>
      ) : (
        <div className="gc-surface">
          <div className="gc-calendar-shell">
            <div className="gc-calendar-shell__body">
              <div className="gc-calendar-shell__frame">
                {(eventsLoading || isFetching) && (
                  <div className="gc-loading-overlay" aria-live="polite" aria-busy="true">
                    <div className="gc-spinner" aria-hidden />
                    <div className="gc-loading-overlay__label">Loading calendar events…</div>
                  </div>
                )}

                <Calendar
                  localizer={localizer}
                  events={events}
                  defaultView="month"
                  views={['month', 'week', 'day']}
                  onRangeChange={(range) => {
                    const next = toEventsPayload(range);
                    setEventsRange((prev) =>
                      prev.timeMin === next.timeMin && prev.timeMax === next.timeMax ? prev : next
                    );
                  }}
                  eventPropGetter={eventStyleGetter}
                  components={{
                    event: ({ event }) => (
                      <Tippy
                        content={<EventTooltip event={event} />}
                        interactive
                        animation="scale"
                        theme="light-border"
                        placement="top-start"
                        appendTo={document.body}
                        delay={[150, 0]}
                        popperOptions={{
                          strategy: 'fixed',
                          modifiers: [
                            {
                              name: 'preventOverflow',
                              options: { boundary: 'viewport', padding: 10 }
                            },
                            {
                              name: 'flip',
                              options: {
                                fallbackPlacements: ['top-start', 'bottom-start', 'right', 'left']
                              }
                            }
                          ]
                        }}
                      >
                        <div className="gc-event-chip" title={event.title}>
                          {!event.allDay ? (
                            <span className="gc-event-chip__time">
                              {moment(event.start).format('LT')}
                            </span>
                          ) : null}
                          <span className="gc-event-chip__title">{event.title}</span>
                        </div>
                      </Tippy>
                    )
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleCalendar;
